import { useSearchParams } from '@forgeglobal/design-framework';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import TransfersInCashDialog from '@tcs/client-portal-common/components/organisms/TransfersIn/TransfersInCashDialog';
import useFeature, {
  FeatureFlag,
} from '@tcs/client-portal-common/hooks/useFeature';
import Button from '@tcs/common/components/atoms/Button';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import useStyles from './style';

interface Props {
  className?: string;
}

const TransfersInCashButton: React.FC<Props> = ({ className }) => {
  const [open, setOpen] = useState(false);
  const [requestCompleted, setRequestCompleted] = useState(false);
  const [requestDeclined, setRequestDeclined] = useState(false);
  const [declinedDismissed, setDeclinedDismissed] = useState(false);
  const TransfersInCashEnabled = useFeature(FeatureFlag.EnableTransfersInCash);
  const classes = useStyles();
  const [searchParams] = useSearchParams();
  const handledTransferStatusRef = useRef<string | null>(null);

  const clearTransferCashQueryParams = () => {
    const url = new URL(window.location.href);
    const hasTransferCashParams =
      url.searchParams.has('transfers-in-cash-requested') ||
      url.searchParams.has('transfers-in-cash-declined');

    if (!hasTransferCashParams) {
      return;
    }

    url.searchParams.delete('transfers-in-cash-requested');
    url.searchParams.delete('transfers-in-cash-declined');

    const search = url.searchParams.toString();
    const nextUrl = `${url.pathname}${search ? `?${search}` : ''}${url.hash}`;
    window.history.replaceState(window.history.state, '', nextUrl);
  };

  useEffect(() => {
    const transferStatusKey = `${searchParams.get('transfers-in-cash-requested') ?? ''}|${searchParams.get('transfers-in-cash-declined') ?? ''}`;

    if (handledTransferStatusRef.current === transferStatusKey) {
      return;
    }

    handledTransferStatusRef.current = transferStatusKey;

    const requestedParam = searchParams.get('transfers-in-cash-requested');
    const requested = requestedParam === 'true';
    const requestedExplicitlyFalse = requestedParam === 'false';
    const declined =
      searchParams.get('transfers-in-cash-declined') === 'true' ||
      requestedExplicitlyFalse;

    setRequestCompleted(requested);
    setRequestDeclined(declined);

    if (declined) {
      setDeclinedDismissed(false);
    }
  }, [searchParams]);

  if (TransfersInCashEnabled) {
    return (
      <>
        <Button
          className={className}
          onClick={() => {
            clearTransferCashQueryParams();
            setRequestCompleted(false);
            setRequestDeclined(false);
            setDeclinedDismissed(true);
            setOpen(true);
          }}
          variant="contained"
          tracking={{ action: 'TransfersInCashStarted' }}>
          Transfer Cash
        </Button>
        <TransfersInCashDialog
          open={open}
          onClose={() => setOpen(false)}
          onSigningResult={(isComplete) => {
            setOpen(false);
            setRequestCompleted(isComplete);
            setRequestDeclined(!isComplete);
            if (!isComplete) {
              setDeclinedDismissed(false);
            }
          }}
        />
        <Dialog open={requestCompleted} maxWidth="sm" fullWidth>
          <DialogContent className={classes.dialogContent}>
            Thank you for initiating your cash transfer. You will receive an
            email when your transfer is successfully processed.
          </DialogContent>
          <DialogActions className={classes.dialogActions}>
            <Button
              onClick={() => {
                clearTransferCashQueryParams();
                setRequestCompleted(false);
              }}>
              Ok
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={requestDeclined && !declinedDismissed}
          maxWidth="sm"
          fullWidth>
          <DialogContent className={classes.dialogContent}>
            Your transfer request has not been submitted. Please complete and
            submit a new DocuSign form when you&apos;re ready.
          </DialogContent>
          <DialogActions className={classes.dialogActions}>
            <Button
              onClick={() => {
                clearTransferCashQueryParams();
                setRequestDeclined(false);
                setDeclinedDismissed(true);
              }}>
              Ok
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  return (
    <Button
      className={className}
      onClick={() => {
        window.open('https://forgetrust.com/forms/fund-your-account', '_blank');
      }}
      variant="contained">
      Transfer Cash
    </Button>
  );
};

export default TransfersInCashButton;
